import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  GraduationCap,
  Save,
  Plus,
  Trash2,
  Globe,
  FileText,
  School,
  Upload
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface VoiceNote {
  id: string;
  duration: number;
  url: string;
  createdAt: Date;
}

interface ExamResult {
  id: string;
  date: string;
  name: string;
  score: string;
  note: string;
}

interface LanguageDoc {
  id: string;
  name: string;
  level: string;
  note: string;
}

interface SchoolApplication {
  id: string;
  schoolName: string;
  department: string;
  username?: string;
  password?: string;
  status: string; // Tamamlandı / Tamamlanmadı
  paymentStatus: string; // Ödendi / Ödenmedi
  result: string; // Kabul / Red
}

interface UniversitalyInfo {
  username?: string;
  password?: string;
  school?: string;
  department?: string;
  status: string; // Tamamlandı
}

interface AddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (studentData: any) => void;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();

  // Determine mode based on user role/unit
  const isStudentMode = user?.unitType === 'operations' || user?.unitType === 'education_consultant' || user?.role === 'admin' || user?.unitType === 'testprep';

  // --- Common State ---
  const [isSaving, setIsSaving] = useState(false);

  // --- Lead Mode State (Original - Simplified for fallback) ---
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    source: 'website',
    priority: 'warm'
  });

  // --- Student Mode State (New) ---
  const [studentForm, setStudentForm] = useState({
    firstName: '',
    lastName: '',
    educationLevel: 'Lise',
    preferredYear: '2025-2026',
    interestedDepartments: [] as string[],
    email: '',
    phone: '',
  });

  const [exams, setExams] = useState<ExamResult[]>([]);
  const [languageDocs, setLanguageDocs] = useState<LanguageDoc[]>([]);
  const [applications, setApplications] = useState<SchoolApplication[]>([]);
  const [universitaly, setUniversitaly] = useState<UniversitalyInfo>({ status: 'Tamamlanmadı' });

  // University State
  const [universities, setUniversities] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchUniversities();
    }
  }, [isOpen]);

  const fetchUniversities = async () => {
    const { data } = await supabase.from('universities').select('id, name').order('name');
    setUniversities(data || []);
  };

  // --- Helper State ---
  const [newProgram, setNewProgram] = useState('');

  // --- Constants ---
  const academicYears = ['2024-2025', '2025-2026', '2026-2027', '2027-2028'];
  const educationLevels = ['Lise', 'Ön Lisans', 'Lisans', 'Yüksek Lisans', 'Doktora'];

  // --- Handlers (Lead Mode) ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // --- Handlers (Student Mode) ---
  const handleStudentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setStudentForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const addExam = () => {
    setExams([...exams, { id: Date.now().toString(), date: '', name: '', score: '', note: '' }]);
  };
  const updateExam = (id: string, field: keyof ExamResult, value: string) => {
    setExams(exams.map(e => e.id === id ? { ...e, [field]: value } : e));
  };
  const removeExam = (id: string) => setExams(exams.filter(e => e.id !== id));

  const addLanguageDoc = () => {
    setLanguageDocs([...languageDocs, { id: Date.now().toString(), name: '', level: '', note: '' }]);
  };
  const updateLanguageDoc = (id: string, field: keyof LanguageDoc, value: string) => {
    setLanguageDocs(languageDocs.map(d => d.id === id ? { ...d, [field]: value } : d));
  };
  const removeLanguageDoc = (id: string) => setLanguageDocs(languageDocs.filter(d => d.id !== id));

  const addApplication = () => {
    setApplications([...applications, { id: Date.now().toString(), schoolName: '', department: '', status: 'Bekliyor', paymentStatus: 'Ödenmedi', result: 'Bekliyor' }]);
  };
  const updateApplication = (id: string, field: keyof SchoolApplication, value: string) => {
    setApplications(applications.map(a => a.id === id ? { ...a, [field]: value } : a));
  };
  const removeApplication = (id: string) => setApplications(applications.filter(a => a.id !== id));

  const addInterestedDepartment = () => {
    if (newProgram.trim() && !studentForm.interestedDepartments.includes(newProgram.trim())) {
      setStudentForm(prev => ({ ...prev, interestedDepartments: [...prev.interestedDepartments, newProgram.trim()] }));
      setNewProgram('');
    }
  };
  const removeInterestedDepartment = (dept: string) => {
    setStudentForm(prev => ({ ...prev, interestedDepartments: prev.interestedDepartments.filter(d => d !== dept) }));
  };

  // --- Submit Handler ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (isStudentMode) {
        // --- Student Mode Submit ---
        const fullStudentData = {
          type: 'student_full', // Marker for StudentList to handle complex save
          // Basic Student Info
          first_name: studentForm.firstName,
          last_name: studentForm.lastName,
          email: studentForm.email,
          phone: studentForm.phone,
          nationality: 'Türkiye', // Default
          // Academic/Lead Info
          education_level: studentForm.educationLevel,
          academic_year: studentForm.preferredYear,
          interested_programs: studentForm.interestedDepartments,
          // Complex Data
          exams: exams,
          language_docs: languageDocs,
          applications: applications,
          universitaly: universitaly,
          // Defaults
          status: 'enrolled', // As they are already students in this flow
          unit_type: user?.unitType || 'operations'
        };

        await onSave(fullStudentData);
        alert(`${studentForm.firstName} ${studentForm.lastName} başarıyla öğrenci olarak eklendi!`);
      } else {
        // --- Lead Mode Submit (Existing Logic) ---
        await onSave({ ...formData, status: 'lead', leadTemperature: 'warm' });
        alert(`${formData.firstName} ${formData.lastName} başarıyla lead olarak eklendi!`);
      }

      onClose();
      resetForms();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert('Hata oluştu.');
    } finally {
      setIsSaving(false);
    }
  };

  const resetForms = () => {
    setStudentForm({ firstName: '', lastName: '', educationLevel: 'Lise', preferredYear: '2025-2026', interestedDepartments: [], email: '', phone: '' });
    setExams([]);
    setLanguageDocs([]);
    setApplications([]);
    setUniversitaly({ status: 'Tamamlanmadı' });
    setFormData({ firstName: '', lastName: '', email: '', phone: '', source: 'website', priority: 'warm' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-6xl mx-4 max-h-[95vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isStudentMode ? 'Yeni Öğrenci Ekle (Operasyon)' : 'Yeni Lead Ekle'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {isStudentMode ? (
            /* --- STUDENT MODE FORM --- */
            <div className="space-y-6">
              {/* 1. Kimlik ve Temel Bilgiler */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2"><User className="w-4 h-4" /> Temel Bilgiler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="firstName" placeholder="Ad *" value={studentForm.firstName} onChange={handleStudentChange} required className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input name="lastName" placeholder="Soyad *" value={studentForm.lastName} onChange={handleStudentChange} required className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input name="email" placeholder="Email" value={studentForm.email} onChange={handleStudentChange} className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input name="phone" placeholder="Telefon" value={studentForm.phone} onChange={handleStudentChange} className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                </div>
              </div>

              {/* 2. Akademik Tercihler */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Akademik Tercihler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm text-gray-500">Eğitim Seviyesi</label>
                    <select name="educationLevel" value={studentForm.educationLevel} onChange={handleStudentChange} className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white">
                      {educationLevels.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Tercih Ettiği Yıl</label>
                    <select name="preferredYear" value={studentForm.preferredYear} onChange={handleStudentChange} className="w-full px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white">
                      {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>

                {/* İlgilendiği Bölümler */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">İlgilendiği Bölümler</label>
                  <div className="flex gap-2 mb-2">
                    <input value={newProgram} onChange={e => setNewProgram(e.target.value)} placeholder="Bölüm adı..." className="flex-1 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white"
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addInterestedDepartment())}
                    />
                    <button type="button" onClick={addInterestedDepartment} className="bg-blue-600 text-white px-3 py-2 rounded-lg"><Plus className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {studentForm.interestedDepartments.map(dept => (
                      <span key={dept} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                        {dept} <X className="w-3 h-3 cursor-pointer" onClick={() => removeInterestedDepartment(dept)} />
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* 3. Sınav Sonuçları */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2"><FileText className="w-4 h-4" /> Sınav Sonuçları</h3>
                  <button type="button" onClick={addExam} className="text-blue-600 text-sm flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Sınav Ekle</button>
                </div>
                <div className="space-y-3">
                  {exams.map((exam) => (
                    <div key={exam.id} className="flex gap-2 items-start flex-wrap sm:flex-nowrap">
                      <input type="date" value={exam.date} onChange={e => updateExam(exam.id, 'date', e.target.value)} className="w-32 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <input placeholder="Sınav Adı (SAT, TOLC)" value={exam.name} onChange={e => updateExam(exam.id, 'name', e.target.value)} className="w-32 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <input placeholder="Puan" value={exam.score} onChange={e => updateExam(exam.id, 'score', e.target.value)} className="w-20 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <input placeholder="Not (Örn: X tarihte girecek)" value={exam.note} onChange={e => updateExam(exam.id, 'note', e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <button type="button" onClick={() => removeExam(exam.id)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {exams.length === 0 && <p className="text-sm text-gray-400 italic">Sınav sonucu eklenmedi.</p>}
                </div>
              </div>

              {/* 4. Dil Belgeleri */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2"><Globe className="w-4 h-4" /> Dil Belgeleri</h3>
                  <button type="button" onClick={addLanguageDoc} className="text-blue-600 text-sm flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Belge Ekle</button>
                </div>
                <div className="space-y-3">
                  {languageDocs.map((doc) => (
                    <div key={doc.id} className="flex gap-2 items-start flex-wrap sm:flex-nowrap">
                      <input placeholder="Belge (IELTS, TOEFL)" value={doc.name} onChange={e => updateLanguageDoc(doc.id, 'name', e.target.value)} className="w-32 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <input placeholder="Seviye (B2, C1)" value={doc.level} onChange={e => updateLanguageDoc(doc.id, 'level', e.target.value)} className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <input placeholder="Not" value={doc.note} onChange={e => updateLanguageDoc(doc.id, 'note', e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-700 bg-white" />
                      <button type="button" onClick={() => removeLanguageDoc(doc.id)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {languageDocs.length === 0 && <p className="text-sm text-gray-400 italic">Dil belgesi eklenmedi.</p>}
                </div>
              </div>

              {/* 5. Okul Başvuruları */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2"><School className="w-4 h-4" /> Okul Başvuruları</h3>
                  <button type="button" onClick={addApplication} className="text-blue-600 text-sm flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Okul Ekle</button>
                </div>
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div key={app.id} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 bg-white dark:bg-gray-800 p-3 rounded border dark:border-gray-600">
                      <select
                        value={app.schoolName}
                        onChange={e => updateApplication(app.id, 'schoolName', e.target.value)}
                        className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 w-full"
                      >
                        <option value="">Okul Seçiniz</option>
                        {universities.map((u: any) => (
                          <option key={u.id} value={u.name}>{u.name}</option>
                        ))}
                      </select>
                      <input placeholder="Bölüm" value={app.department} onChange={e => updateApplication(app.id, 'department', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" />
                      <input placeholder="Kullanıcı Adı" value={app.username} onChange={e => updateApplication(app.id, 'username', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" />
                      <input placeholder="Şifre" value={app.password} onChange={e => updateApplication(app.id, 'password', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600" />

                      <select value={app.status} onChange={e => updateApplication(app.id, 'status', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600">
                        <option value="Bekliyor">Durum: Bekliyor</option>
                        <option value="Tamamlandı">Tamamlandı</option>
                        <option value="Tamamlanmadı">Tamamlanmadı</option>
                      </select>
                      <select value={app.paymentStatus} onChange={e => updateApplication(app.id, 'paymentStatus', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600">
                        <option value="Ödenmedi">Ödenmedi</option>
                        <option value="Ödendi">Ödendi</option>
                      </select>
                      <select value={app.result} onChange={e => updateApplication(app.id, 'result', e.target.value)} className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600">
                        <option value="Bekliyor">Sonuç: Bekliyor</option>
                        <option value="Kabul">Kabul</option>
                        <option value="Red">Red</option>
                      </select>
                      <button type="button" onClick={() => removeApplication(app.id)} className="text-red-500 text-xs text-right col-span-1 md:col-span-2 lg:col-span-4 hover:underline">Kaydı Sil</button>
                    </div>
                  ))}
                  {applications.length === 0 && <p className="text-sm text-gray-400 italic">Başvuru eklenmedi.</p>}
                </div>
              </div>

              {/* 6. Universitaly */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-700">
                <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">Universitaly Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input placeholder="Kullanıcı Adı" value={universitaly.username || ''} onChange={e => setUniversitaly({ ...universitaly, username: e.target.value })} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input placeholder="Şifre" value={universitaly.password || ''} onChange={e => setUniversitaly({ ...universitaly, password: e.target.value })} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input placeholder="Okul" value={universitaly.school || ''} onChange={e => setUniversitaly({ ...universitaly, school: e.target.value })} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <input placeholder="Bölüm" value={universitaly.department || ''} onChange={e => setUniversitaly({ ...universitaly, department: e.target.value })} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white" />
                  <select value={universitaly.status || 'Tamamlanmadı'} onChange={e => setUniversitaly({ ...universitaly, status: e.target.value })} className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 bg-white">
                    <option value="Tamamlanmadı">Tamamlanmadı</option>
                    <option value="Tamamlandı">Tamamlandı</option>
                  </select>
                </div>
              </div>

              {/* 7. Belgeler */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2"><Upload className="w-4 h-4" /> Belgeler</h3>
                <p className="text-sm text-gray-500 mb-4">Öğrenci oluşturulduktan sonra detay sayfasından belge yükleyebilirsiniz.</p>
                <div className="flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 bg-white dark:bg-gray-800">
                  <span className="text-gray-500">Dosya yükleme alanı (Öğrenci oluşturulduktan sonra aktif olur)</span>
                </div>
              </div>

            </div>
          ) : (
            /* --- DEFAULT LEAD FORM --- */
            <div className="space-y-6">
              <p className="text-sm text-gray-500 mb-4 bg-yellow-50 p-2 rounded border border-yellow-200">
                Standart Lead Ekleme Formu
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="firstName" placeholder="Ad" value={formData.firstName} onChange={handleChange} required className="px-3 py-2 border rounded" />
                <input name="lastName" placeholder="Soyad" value={formData.lastName} onChange={handleChange} required className="px-3 py-2 border rounded" />
                <input name="email" placeholder="Email" value={formData.email} onChange={handleChange} className="px-3 py-2 border rounded" />
                <input name="phone" placeholder="Telefon" value={formData.phone} onChange={handleChange} className="px-3 py-2 border rounded" />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button type="button" onClick={onClose} className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors">İptal</button>
            <button type="submit" disabled={isSaving} className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSaving ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Kaydediliyor...' : (isStudentMode ? 'Öğrenci Kaydet' : 'Lead Kaydet')}</span>
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default AddStudentModal;